import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import FilterContextVirtualTable from '../../../components/Filter/FilterContextVirtualTable';
import FilterResultNoItemsScreen from '../../../components/Filter/FilterResultNoItemsScreen';
import useFilterContext from '../../../components/Filter/hooks/useFilterContext';
import { OptimisticWriteFunction } from '../../../components/Filter/types';
import { makePrioStyles } from '../../../theme/utils';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import { useQuery } from '@tanstack/react-query';
import { apiFetchContactTags } from '../api';
import {
  ContactCompanyData,
  ContactCompanysCalculatedData,
  ContactSearchResultItem,
} from '../../../models/Contact';
import { useExportContactCompaniesToExcelFile } from '../export';
import { exportToCsv } from '../../../util/export';
import { ButtonProps } from '@prio365/prio365-react-library';
import useCompaniesContext from '../../companies/hooks/useCompaniesContext';

const useStyles = makePrioStyles((theme) => ({
  root: {
    flex: 1,
  },
  cellCentered: {
    justifyItems: 'center',
  },
  row: {
    cursor: 'pointer',
    '& button': {
      visibility: 'hidden',
    },
    '&:hover button': {
      visibility: 'visible',
    },
  },
}));

export interface ContactSearchPageTableRef {
  getSelectedContacts: () => ContactSearchResultItem[];
  optimisticWrite: OptimisticWriteFunction<
    ContactCompanyData,
    ContactCompanysCalculatedData
  >;
}

interface ContactSearchPageTableProps {
  className?: string;
  tableId: string;
  onRowClick?: (entry: ContactSearchResultItem) => void;
  onRowSelectionChange?: (
    selectedSearchItems: ContactSearchResultItem[]
  ) => void;
  selectedSearchItems: ContactSearchResultItem[];
}

export const ContactSearchPageTable = forwardRef(
  (
    props: ContactSearchPageTableProps,
    ref: React.Ref<ContactSearchPageTableRef>
  ) => {
    //#region ------------------------------ Defaults
    const {
      className,
      tableId,
      selectedSearchItems,
      onRowClick,
      onRowSelectionChange,
    } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const { getCompanyById } = useCompaniesContext();
    const { data, isLoading, optimisticWrite } = useFilterContext<
      ContactCompanyData,
      ContactCompanysCalculatedData
    >();

    const { data: contactTags } = useQuery({
      queryKey: ['contactTags'],
      queryFn: () => apiFetchContactTags(),
      staleTime: 1000 * 60 * 60 * 20, // 20 hours
    });
    //#endregion

    //#region ------------------------------ Methods / Handlers
    const onSelectionChange = (items: ContactSearchResultItem[]) => {
      if (onRowSelectionChange) {
        onRowSelectionChange(items);
      }
    };

    const handleOnRow: (
      item: ContactSearchResultItem
    ) => VirtualListItemOnRowProps = useCallback(
      (item) => {
        return {
          onClick: (e) => {
            if (onRowClick) {
              onRowClick(item);
            }
          },
          className: classes.row,
        };
      },
      [classes, onRowClick]
    );

    const exportContactCompaniesToExcelFile =
      useExportContactCompaniesToExcelFile();

    const exportContactCompaniesToCsvFile = () => {
      const items =
        selectedSearchItems.length > 0 ? selectedSearchItems : data?.items;
      const csvHeader = [
        t(`contacts:form.labels.firstName`),
        t(`contacts:form.labels.lastName`),
        t(`contacts:form.labels.title`),
        t(`contacts:form.labels.gender`),
        t(`contacts:form.labels.eMail`),
        t(`contacts:form.labels.eMail2`),
        t(`contacts:form.labels.phone`),
        t(`contacts:form.labels.phone2`),
        t(`contacts:form.labels.cellphone`),
        t(`contacts:form.labels.cellphone2`),
        t(`contacts:form.labels.fax`),
        t(`contacts:form.labels.notes`),
        t(`contacts:form.labels.contactType`),
        t(`contacts:form.labels.contactTags`),
        t(`contacts:form.labels.companyName`),
        t(`contacts:form.labels.street`),
        t(`contacts:form.labels.zip`),
        t(`contacts:form.labels.city`),
        t(`contacts:form.labels.federalStateCode`),
        t(`contacts:form.labels.countryCode`),
        t(`companies:form.labels.shortName`),
        t(`companies:form.labels.fullName`),
        t(`companies:form.labels.fullName2`),
        t(`companies:form.labels.website`),
        t(`companies:form.labels.notesCompany`),
        t(`companies:companyType.title`),
        t(`companies:form.labels.address`),
      ];
      const csvData = items?.map((item) => {
        const office = getCompanyById(
          item.data.contact?.companyId
        )?.offices.find(
          (_office) => _office.officeId === item.data.contact.officeId
        );

        return [
          item.data.contact?.firstName ?? '',
          item.data.contact?.lastName ?? '',
          item.data.contact?.title ?? '',
          item.data.contact?.gender === 'male'
            ? t(`contacts:form.gender.male`)
            : item.data.contact?.gender === 'female'
              ? t(`contacts:form.gender.female`)
              : item.data.contact?.gender === 'diverse'
                ? t(`contacts:form.gender.diverse`)
                : '',
          item.calculated.email ?? '',
          item.data.contact?.eMail2 ?? '',
          item.calculated.phone ?? '',
          item.data.contact?.phone2 ?? '',
          item.data.contact?.cellphone ?? '',
          item.data.contact?.cellphone2 ?? '',
          item.data.contact?.fax ?? '',
          item.data.contact?.notes ?? '',
          item.data.contact?.contactType === 'InternalContact'
            ? t(`contacts:form.contactType.internalContact`)
            : item.data.contact?.contactType === 'ExternalContact'
              ? t(`contacts:form.contactType.externalContact`)
              : '',
          item.calculated.contactTagIds
            .map((tagId) => {
              const tag = contactTags?.data?.find(
                (t) => t.contactTagId === tagId
              );
              return tag?.name;
            })
            .join(', '),
          getCompanyById(item.data.contact?.companyId)?.fullName ?? '',
          office?.street ?? '',
          office?.zip ?? '',
          office?.city ?? '',
          office?.federalStateCode ?? '',
          office?.countryCode ?? '',
          item.data.company?.shortName ?? '',
          item.data.company?.fullName ?? '',
          item.data.company?.fullName2 ?? '',
          item.data.company?.website ?? '',
          item.data.company?.notes ?? '',
          item.data.company?.companyType === 'internalCompany'
            ? t(`companies:companyType.internalCompany`)
            : item.data.company?.companyType === 'externalCompany'
              ? t(`companies:companyType.externalCompany`)
              : item.data.company?.companyType === 'ContactCompany'
                ? t(`companies:companyType.contactCompany`)
                : '',

          item.calculated.address ?? '',
        ];
      });
      exportToCsv(csvHeader, csvData, `${t('contacts:title')}.csv`);
    };

    const handleOnExportExcelClick = () => {
      const items =
        selectedSearchItems.length > 0 ? selectedSearchItems : data?.items;
      exportContactCompaniesToExcelFile(items || [], contactTags);
    };

    const handleOnExportCsvClick = () => {
      exportContactCompaniesToCsvFile();
    };

    const checkEquality = (
      a: ContactSearchResultItem,
      b: ContactSearchResultItem
    ) => {
      const aIsContact = a.data?.contact?.contactId;
      const bIsContact = b.data?.contact?.contactId;
      if (aIsContact && bIsContact) {
        return a.data.contact.contactId === b.data.contact.contactId;
      }
      if (!aIsContact && !bIsContact) {
        return (
          a.data.company.companyId === b.data.company.companyId &&
          a.calculated.name === b.calculated.name
        );
      }
      return false;
    };
    //#endregion

    //#region ------------------------------ Columns
    const columns: Column<ContactSearchResultItem>[] = [
      {
        Cell: ({
          originalData: {
            calculated: { companyContact },
          },
        }) => {
          return (
            <div title={companyContact === 'Company' ? 'Company' : 'Contact'}>
              <FontAwesomeIcon
                icon={[
                  'fal',
                  companyContact === 'Company' ? 'building' : 'user',
                ]}
              />
            </div>
          );
        },
        title: () => <div></div>,
        width: 4,
        minWidth: 34,
        id: 'status',
        accessor: 'calculated.companyContact',
        className: classNames(classes.cellCentered),
        sortingFn: (rowA, rowB) => {
          const contactA = rowA.calculated.companyContact;
          const contactB = rowB.calculated.companyContact;
          if (contactA === contactB) return 0;
          if (contactA === 'Contact') return -1;
          return 1;
        },
        alignSelf: true,
      },
      {
        id: 'name',
        width: 15,
        title: t('contacts:searchTable.header.name'),
        accessor: 'calculated.name',
        sortingFn: (a, b) => a.calculated.name.localeCompare(b.calculated.name),
        Cell: ({
          originalData: {
            calculated: { name },
          },
        }) => `${name}`,
        alignSelf: true,
      },
      {
        title: t('contacts:searchTable.header.email'),
        width: 25,
        id: 'email',
        accessor: 'calculated.email',
        sortingFn: (a, b) =>
          a.calculated.email.localeCompare(b.calculated.email),
        Cell: ({
          originalData: {
            calculated: { email },
          },
        }) => `${email}`,
        alignSelf: true,
      },
      {
        Cell: ({
          originalData: {
            calculated: { phone },
          },
        }) => {
          return phone;
        },
        title: t('contacts:searchTable.header.phone'),
        width: 15,
        id: 'phone',
        accessor: 'calculated.phone',
        cellTitle: (contact) => {
          return `${contact.calculated.phone}`;
        },
        alignSelf: true,
        sortingFn: (a, b) =>
          a.calculated.phone.localeCompare(b.calculated.phone),
      },
      {
        Cell: ({
          originalData: {
            calculated: { address },
          },
        }) => {
          return address;
        },
        title: t('contacts:searchTable.header.address'),
        width: 18,
        id: 'address',
        accessor: 'calculated.address',
        cellTitle: (contact) => {
          return `${contact.calculated.address}`;
        },
        alignSelf: true,
        sortingFn: (a, b) =>
          a.calculated.address.localeCompare(b.calculated.address),
      },
      {
        title: t('contacts:searchTable.header.contactTags'),
        width: 22,
        id: 'contactTags',
        accessor: 'calculated.contactTagIds',
        Cell: ({
          originalData: {
            calculated: { contactTagIds },
          },
        }) => {
          const tags = contactTagIds.map((tagId) => {
            const tag = contactTags?.data?.find(
              (t) => t.contactTagId === tagId
            );
            return tag?.name;
          });
          return `${tags.join(', ')}`;
        },
        alignSelf: true,
      },
    ];
    //#endregion

    //#region ------------------------------ Effects
    useImperativeHandle(ref, () => ({
      getSelectedContacts: () => {
        return selectedSearchItems;
      },
      optimisticWrite,
    }));
    //#endregion

    return (
      <FilterContextVirtualTable<ContactSearchResultItem>
        id={tableId}
        className={classNames(classes.root, className)}
        columns={columns}
        data={data?.items || []}
        selectedItems={selectedSearchItems}
        resizable="relative"
        onRow={handleOnRow}
        onSelectionChange={onSelectionChange}
        onCheckEquality={checkEquality}
        noItemsScreen={<FilterResultNoItemsScreen />}
        loading={
          isLoading && {
            type: 'noItems',
          }
        }
        rowsAreSelectable
        actionBarButtons={[
          {
            children: t('common:actions.excelExportSelection'),
            iconProp: 'file-excel',
            onClick: handleOnExportExcelClick,
          },
          {
            children: t('common:actions.csvExportSelection'),
            iconProp: 'file-arrow-down',
            onClick: handleOnExportCsvClick,
          },
        ]}
        actionBarDefaultButtons={[
          ...(data?.totalItems > 0
            ? ([
                {
                  children: t('common:actions.csvExport'),
                  iconProp: 'file-arrow-down',
                  onClick: handleOnExportCsvClick,
                },
                {
                  children: t('common:actions.excelExport'),
                  iconProp: 'file-excel',
                  onClick: handleOnExportExcelClick,
                },
              ] as Array<ButtonProps>)
            : []),
        ]}
      />
    );
  }
);

export default ContactSearchPageTable;
